import React, { useState } from 'react';

/*
 * ─── GALLERY IMAGES ──────────────────────────────────────────────────────────
 * Add your before/after photos to the public/images/ folder.
 * Name them exactly as shown below, then they will appear automatically.
 *
 *   public/images/bedroom-before.jpg   public/images/bedroom-after.jpg
 *   public/images/bathroom-before.jpg  public/images/bathroom-after.jpg
 *   public/images/toilet-before.jpg    public/images/toilet-after.jpg
 *   public/images/kitchen-before.jpg   public/images/kitchen-after.jpg
 *
 * Recommended size: 600×600px square crops work best.
 * If a file is missing the emoji placeholder shows instead — no crashes.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const PAIRS = [
  { label: 'Bathroom',  beforeImg: '/images/bedroom-before.jpg',  afterImg: '/images/bedroom-after.jpg',  beforeEmoji: '🛏', afterEmoji: '✨' },
  { label: 'Bedroom', beforeImg: '/images/bathroom-before.jpg', afterImg: '/images/bathroom-after.jpg', beforeEmoji: '🚿', afterEmoji: '✨' },
  { label: 'Kitchen',   beforeImg: '/images/toilet-before.jpg',   afterImg: '/images/toilet-after.jpg',   beforeEmoji: '🚽', afterEmoji: '✨' },
  { label: 'Toilet',  beforeImg: '/images/new-before.jpg',  afterImg: '/images/new-after.jpg',  beforeEmoji: '🍽', afterEmoji: '✨' },
];

function GalleryItem({ label, beforeImg, afterImg, beforeEmoji, afterEmoji }) {
  const [showAfter, setShowAfter] = useState(false);
  const [beforeErr, setBeforeErr] = useState(false);
  const [afterErr, setAfterErr]   = useState(false);

  const useBeforeEmoji = beforeErr;
  const useAfterEmoji  = afterErr;

  return (
    <div
      onClick={() => setShowAfter(s => !s)}
      style={{
        aspectRatio: '1',
        position: 'relative', overflow: 'hidden', cursor: 'pointer',
        background: showAfter ? '#16536e' : 'var(--blue)',
        transition: 'background 0.4s',
      }}>

      {/* BEFORE image / emoji */}
      {!showAfter && (
        useBeforeEmoji
          ? <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 40, transform: 'scale(1)', transition: 'transform 0.35s ease' }}>{beforeEmoji}</span>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>{label}</p>
            </div>
          : <img
              src={beforeImg} alt={`${label} before cleaning`}
              onError={() => setBeforeErr(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s', transform: 'scale(1)' }}
            />
      )}

      {/* AFTER image / emoji */}
      {showAfter && (
        useAfterEmoji
          ? <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 40, transform: 'scale(1.15) rotate(10deg)', transition: 'transform 0.35s ease' }}>{afterEmoji}</span>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>{label}</p>
            </div>
          : <img
              src={afterImg} alt={`${label} after cleaning`}
              onError={() => setAfterErr(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s', transform: 'scale(1.04)' }}
            />
      )}

      {/* Before / After badge */}
      <div style={{
        position: 'absolute', top: 10, left: 10,
        background: showAfter ? 'rgba(22,163,74,0.85)' : 'rgba(0,0,0,0.55)',
        color: '#fff', fontSize: '0.65rem', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: 1,
        padding: '3px 8px', borderRadius: 4,
        transition: 'background 0.3s',
      }}>
        {showAfter ? 'After' : 'Before'}
      </div>

      {/* Room label (shown on images) */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
        padding: '20px 10px 8px',
        color: '#fff', fontSize: '0.75rem', fontWeight: 600,
        letterSpacing: '0.5px',
      }}>{label}</div>

      {/* Tap hint */}
      <div style={{
        position: 'absolute', bottom: 8, right: 10,
        fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', letterSpacing: 0.5,
      }}>tap to toggle</div>
    </div>
  );
}

export default function Gallery() {
  return (
    <section id="gallery" style={{ background: 'var(--navy)', padding: '100px 5%', textAlign: 'center' }}>
      <div style={{ maxWidth: 560, margin: '0 auto 48px' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: 12 }}>Gallery</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', color: '#fff', lineHeight: 1.25, marginBottom: 14 }}>
          Before &amp; After
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.97rem', lineHeight: 1.7 }}>
          See the SE1 HostCare difference. Tap any tile to reveal the transformation.
        </p>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 3, maxWidth: 1000, margin: '0 auto',
        borderRadius: 16, overflow: 'hidden',
      }}
        className="gallery-grid">
        {PAIRS.map(p => <GalleryItem key={p.label} {...p} />)}
      </div>

      <style>{`
        @media (max-width: 600px) {
          .gallery-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}
